import { type Component, markRaw, shallowReactive } from 'vue'
import type { Router } from 'vue-router'

/**
 * The four layouts, each its own lazy chunk.
 *
 * A first-time marketplace visitor on Nigerian mobile data must not download the dashboard
 * shell — its sidebar, topbar, workspace switcher and account menu are useless to them, and
 * eagerly importing all four layouts put 18KB gzipped of dashboard chrome into the entry
 * chunk, over the phase-01 budget. Each layout is one small chunk fetched when a route asks
 * for it.
 *
 * They are loaded **by the router, before a navigation commits** (`preloadLayouts`), exactly as
 * a lazy page is — not by `defineAsyncComponent` at render time. Rendered async, the layout was
 * fetched only after the page had resolved and committed, and for that second round-trip the
 * app painted nothing: a first visit sat on an empty screen until the chrome arrived, and a jump
 * from the marketplace to a dashboard blanked out mid-navigation. Loaded here, the page and its
 * layout arrive together, the old screen stays up until they have, and `App.vue` only ever
 * renders a layout that is already in memory.
 */
const loaders = {
  dashboard: () => import('~/layouts/DashboardLayout.vue'),
  marketplace: () => import('~/layouts/MarketplaceLayout.vue'),
  auth: () => import('~/layouts/AuthLayout.vue'),
  blank: () => import('~/layouts/BlankLayout.vue'),
} satisfies Record<string, () => Promise<{ default: Component }>>

export type LayoutName = keyof typeof loaders

/** The layout a route's `meta.layout` names. Anything unset or unknown gets the blank one. */
export function layoutFor(meta: string | undefined): LayoutName {
  return meta !== undefined && Object.hasOwn(loaders, meta) ? (meta as LayoutName) : 'blank'
}

const loaded = shallowReactive<Partial<Record<LayoutName, Component>>>({})
const inFlight = new Map<LayoutName, Promise<void>>()

/** The layout, if its chunk has arrived. Reactive: a render reading it updates when it lands. */
export function loadedLayout(name: LayoutName): Component | undefined {
  return loaded[name]
}

/**
 * Fetch a layout's chunk once. Concurrent callers share the request; a failed one is forgotten,
 * so the next navigation retries it rather than inheriting the rejection.
 */
export function loadLayout(name: LayoutName): Promise<void> {
  if (loaded[name]) return Promise.resolve()
  let pending = inFlight.get(name)
  if (!pending) {
    pending = loaders[name]()
      .then((module) => {
        loaded[name] = markRaw(module.default)
      })
      .finally(() => inFlight.delete(name))
    inFlight.set(name, pending)
  }
  return pending
}

/**
 * Hold every navigation until its layout is loaded, as vue-router already does for a lazy page.
 *
 * `beforeResolve` runs after every redirect, so only the layout that is actually shown is
 * fetched. The guard returns nothing, which lets the navigation continue; a chunk that fails to
 * load fails the navigation, exactly as a page chunk does.
 */
export function preloadLayouts(router: Router) {
  router.beforeResolve(async (to) => {
    await loadLayout(layoutFor(to.meta.layout))
  })
}
